import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Selector from '../../Select/select-component.js';
import GradientText from '../../Text/gradient-text-component.js';
import { Modal } from '../../Modal/modal-component.js';
import SignatureCanvas from 'react-signature-canvas';
import ContactService from '../../../Services/ContactService.js';
import { getTermsSections, getTermsLabels, getHostingPlans } from '../../../Public/terms-hosting-data.js';

/**
 * ✅ V: TermsHostingForm - Refactored for flowma Universal Engine
 * Standardized to send { answers, metadata } with binary attachments.
 */
function TermsHostingForm({ onSuccess }) {
    const systemLanguage = useSelector((state) => state.systemData?.language || 'he');
    const isRtl = systemLanguage === 'he';

    const labels = getTermsLabels(systemLanguage);
    const planOptions = getHostingPlans(systemLanguage);
    const sections = getTermsSections(systemLanguage);

    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [planCode, setPlanCode] = useState('');
    const [agree, setAgree] = useState(false);

    const sigRef = useRef(null);
    const [sigOk, setSigOk] = useState(false);
    const planRequiredRef = useRef(null);
    const sigRequiredRef = useRef(null);

    const [submitting, setSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState('idle');
    const [transferring, setTransferring] = useState(false);
    const [transferProgress, setTransferProgress] = useState(0);

    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [termsRead, setTermsRead] = useState(false);
    const termsScrollRef = useRef(null);

    useEffect(() => {
        if (!planRequiredRef.current) return;
        planRequiredRef.current.setCustomValidity(planCode ? '' : labels.planPrompt);
    }, [planCode, labels.planPrompt]);

    useEffect(() => {
        if (!showConfirmationModal || submitStatus === 'idle') return;
        const t = setTimeout(() => {
            setShowConfirmationModal(false);
            setSubmitStatus('idle');
        }, 1500);
        return () => clearTimeout(t);
    }, [showConfirmationModal, submitStatus]);

    useEffect(() => {
        if (!transferring) return;
        setTransferProgress(0);
        const start = setTimeout(() => setTransferProgress(100), 50);
        return () => clearTimeout(start);
    }, [transferring]);

    const updateSigValidity = () => {
        let signed = false;
        if (sigRef.current && typeof sigRef.current.isEmpty === 'function') {
            signed = !sigRef.current.isEmpty();
        }
        setSigOk(signed);
        if (sigRequiredRef.current) {
            sigRequiredRef.current.setCustomValidity(signed ? '' : labels.signaturePrompt);
        }
    };

    const handleTermsScroll = () => {
        const el = termsScrollRef.current;
        if (!el) return;
        const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
        if (nearBottom && !termsRead) {
            setTimeout(() => {
                setTermsRead(true);
                setAgree(true);
                setShowTermsModal(false);
            }, 2200);
        }
    };

    const onClearSignature = () => {
        if (sigRef.current) sigRef.current.clear();
        setSigOk(false);
        updateSigValidity();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        updateSigValidity();

        if (!e.currentTarget.checkValidity()) {
            const firstInvalid = e.currentTarget.querySelector(':invalid');
            if (firstInvalid) firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }

        setSubmitting(true);
        setSubmitStatus('idle');

        try {
            let signatureFile = null;
            let signatureBase64 = null;

            if (sigRef.current && !sigRef.current.isEmpty()) {
                signatureBase64 = sigRef.current.getCanvas().toDataURL('image/png');
                const response = await fetch(signatureBase64);
                const blob = await response.blob();
                signatureFile = new File([blob], `signature_${Date.now()}.png`, { type: 'image/png' });
            }

            // ✅ V: Standardized payload for TransactionService.sendAgreementEmail
            const payload = {
                answers: {
                    fullName,
                    email,
                    planCode: planOptions.find(o => o.id === planCode)?.title || planCode,
                    termsAccepted: agree ? "Yes" : "No",
                    signaturePng: signatureBase64 // Sent for internal HTML generation
                },
                metadata: {
                    subject: `Hosting Agreement: ${fullName}`,
                    source: "Hosting Terms Agreement",
                    lang: systemLanguage
                }
            };

            // ✅ V: Using submitAgreement which now uses the standardized _dispatch helper
            await ContactService.submitAgreement(payload, signatureFile ? [signatureFile] : []);

            setSubmitStatus('success');
            setShowConfirmationModal(true);
            onSuccess?.();

            setTransferring(true);
            setTimeout(() => {
                window.location.href = 'https://pay.grow.link/0e1ed3e6ca1f0c7d3deb58924d2ec5ad-MjYxNjA4Ng';
            }, 1700);

            // Reset Form
            setFullName('');
            setEmail('');
            setPlanCode('');
            setAgree(false);
            onClearSignature();

        } catch (err) {
            console.error("Submission Error:", err);
            setSubmitStatus('error');
            setShowConfirmationModal(true);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className='flex w-full lg:justify-start justify-center items-center text-black' dir={isRtl ? 'rtl' : 'ltr'}>
             {/* Progress Bar & Form Content remains identical to your design */}
             {/* ... UI code starts here ... */}
            {transferring && (
                <>
                    <div className="fixed top-0 left-0 w-full z-[100]">
                        <div className="h-1.5 bg-slate-200/80 dark:bg-slate-800/80">
                            <div
                                className="h-full bg-purple-600 transition-[width] duration-[1600ms] ease-out"
                                style={{ width: transferProgress + '%' }}
                            />
                        </div>
                    </div>
                    <div className="fixed top-3 left-1/2 -translate-x-1/2 z-[101]">
                        <div className="px-4 py-2 rounded-full bg-white/90 dark:bg-slate-900/90 shadow-lg backdrop-blur">
                            <span className="inline-flex items-center gap-2 text-sm lg:text-base font-medium">
                                <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                <span>{labels.redirecting}</span>
                            </span>
                        </div>
                    </div>
                </>
            )}

            <form className='flex w-full flex-col justify-start max-w-3xl space-y-10' onSubmit={handleSubmit}>
                <h1 className={`lg:text-7xl text-5xl font-medium  text-center ${isRtl ? 'lg:text-right' : 'lg:text-left'}`}>
                    <GradientText>{labels.title}</GradientText>
                </h1>

                <div className={`flex items-center w-full my-4 justify-start ${isRtl ? 'lg:text-right' : 'lg:text-left'}`}>
                    <h2 className="lg:w-4/5 w-full text-center lg:text-start lg:text-2xl text-xl font-normal ">
                        {labels.subtitle}
                    </h2>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 w-full !text-2xl">
                    <div className="flex flex-col lg:flex-row gap-8 sm:col-span-2">
                        <div className="flex-1">
                            <input
                                type="text"
                                className="input-component !text-2xl w-full"
                                placeholder={labels.namePlaceholder}
                                value={fullName}
                                onChange={(e) => setFullName(e.target.value)}
                                required
                            />
                        </div>
                        <div className="flex-1">
                            <input
                                dir="ltr"
                                type="email"
                                className="input-component !text-2xl w-full"
                                placeholder={labels.emailPlaceholder}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </div>
                    </div>

                    <div className="sm:col-span-2">
                        <Selector
                            size="xl"
                            options={planOptions}
                            value={planOptions.find(o => o.id === planCode)?.title || ''}
                            onChange={(val) => {
                                const found = planOptions.find(o => o.title === val);
                                if (found) setPlanCode(found.id);
                            }}
                        />
                        <input ref={planRequiredRef} className="sr-only" required value={planCode} onChange={() => { }} />
                    </div>

                    <div className="sm:col-span-2">
                        <label className="mt-2 flex items-start gap-3 text-base text-slate-700 cursor-pointer">
                            <input
                                type="checkbox"
                                className="mt-1 h-5 w-5 rounded border-slate-300"
                                checked={agree}
                                onChange={(e) => {
                                    if (e.target.checked && !termsRead) {
                                        setShowTermsModal(true);
                                        return;
                                    }
                                    setAgree(e.target.checked);
                                }}
                                required
                            />
                            <div className={isRtl ? 'text-right' : 'text-left'}>{labels.termsCheckbox}</div>
                        </label>
                    </div>

                    <div className="sm:col-span-2">
                        <div className="rounded-3xl border border-slate-300 bg-white">
                            <SignatureCanvas
                                ref={sigRef}
                                penColor="#111827"
                                canvasProps={{ className: 'w-full h-44 rounded' }}
                                onBegin={updateSigValidity}
                                onEnd={updateSigValidity}
                            />
                        </div>
                        <div className="flex gap-3 mt-2 py-2 ">
                            <button type="button" onClick={onClearSignature} className="button-component-white lg:max-w-xs">{labels.clearSignature}</button>
                        </div>
                        <input ref={sigRequiredRef} className="sr-only" required value={sigOk ? 'signed' : ''} onChange={() => { }} />
                    </div>
                </div>

                <div className='flex w-full items-center justify-center'>
                    <div className='flex justify-center w-full lg:max-w-sm'>
                        <button type="submit" disabled={submitting} className="button-component w-full lg:text-4xl text-3xl lg:px-20 !py-4 flex items-center shadow-md">
                            <span className="flex items-center gap-3">
                                <span>{submitting ? labels.submitting : (submitStatus === 'success' ? labels.sent : labels.submit)}</span>
                                {submitting && (
                                    <span className="inline-block w-6 h-6 border-4 border-current border-t-transparent rounded-full animate-spin" />
                                )}
                            </span>
                        </button>
                    </div>
                </div>
            </form>

            {/* Modals for Terms and Success remain as they were */}
            <Modal expand={showTermsModal} timeout={false} disable={false}>
                <div className="flex w-full h-full bg-white dark:bg-black flex-col justify-center items-center px-4 py-6 overflow-y-auto">
                    <div className="w-full max-w-3xl bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 p-6">
                        <h2 className="text-3xl lg:text-4xl font-bold text-center mb-6 text-slate-900 dark:text-white">
                            {labels.termsTitle}
                        </h2>
                        <div
                            ref={termsScrollRef}
                            onScroll={handleTermsScroll}
                            className={`max-h-[70vh] overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-xl p-6 text-slate-800 dark:text-slate-100 space-y-6 prose prose-slate dark:prose-invert leading-relaxed ${isRtl ? 'text-right' : 'text-left'}`}
                        >
                            {sections.map((section, idx) => (
                                <section key={idx}>
                                    <h3 className="text-xl font-semibold mb-2">{section.title}</h3>
                                    {section.content && <p>{section.content}</p>}
                                    {section.list && (
                                        <ul className={`list-disc list-inside space-y-1 ${isRtl ? 'pr-2' : 'pl-2'}`}>
                                            {section.list.map((item, i) => <li key={i}>{item}</li>)}
                                        </ul>
                                    )}
                                </section>
                            ))}
                        </div>
                        <div className="flex justify-center mt-6">
                            <button
                                onClick={() => {
                                    setTermsRead(true);
                                    setAgree(true);
                                    setShowTermsModal(false);
                                }}
                                className="button-component lg:text-2xl text-xl px-10 py-3 shadow-md"
                            >
                                {labels.termsAgreeBtn}
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
}

export default TermsHostingForm;